import { describePhoneConflict, type PhoneConflict } from "@/lib/start-lead-conversation";
import { normalizeImportPhone, stripBrDialPrefixes } from "@/lib/import-phone";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

const COUNTRY_CODES = [
  { code: "55", flag: "🇧🇷", name: "Brasil", placeholder: "11999999999" },
  { code: "1", flag: "🇺🇸", name: "EUA", placeholder: "2125551234" },
  { code: "351", flag: "🇵🇹", name: "Portugal", placeholder: "912345678" },
  { code: "54", flag: "🇦🇷", name: "Argentina", placeholder: "1123456789" },
  { code: "598", flag: "🇺🇾", name: "Uruguai", placeholder: "94123456" },
  { code: "595", flag: "🇵🇾", name: "Paraguai", placeholder: "981123456" },
  { code: "56", flag: "🇨🇱", name: "Chile", placeholder: "912345678" },
  { code: "57", flag: "🇨🇴", name: "Colômbia", placeholder: "3101234567" },
  { code: "52", flag: "🇲🇽", name: "México", placeholder: "5512345678" },
] as const;

function detectCountryFromPhone(phone: string): { code: string; digits: string } {
  const cleaned = phone.replace(/\D/g, "");
  // Try longest codes first
  for (const c of [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length)) {
    if (cleaned.startsWith(c.code)) {
      return { code: c.code, digits: cleaned.slice(c.code.length) };
    }
  }
  return { code: "55", digits: cleaned };
}
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { insertDraftLeadPhones, LeadExtraPhonesEditor } from "@/components/leads/LeadExtraPhonesEditor";
import type { Json } from "@/integrations/supabase/types";
import { useToast } from "@/hooks/use-toast";
import { useResourceLimits } from "@/hooks/useResourceLimits";
import { useMemberPermissions } from "@/hooks/useMemberPermissions";
import type { Stage, SalesFunnel, LeadTag } from "@/types/lead";
import { Checkbox } from "@/components/ui/checkbox";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { maskDocument, detectDocumentType, type DocumentType } from "@/lib/document-mask";
import { resolveFunnelDisplayName } from "@/lib/funnel-display-label";
import { useWorkspaceIntegrationsMap } from "@/hooks/useWorkspaceIntegrationsMap";
import { persistLeadFunnelPlacement } from "@/lib/lead-funnel-placement";
import { humanizeSupabaseError } from "@/lib/supabase-errors";
import { LeadCustomFieldsInputs } from "@/components/leads/LeadCustomFieldsInputs";
import { customFieldRowsFromMetadata, mergeCustomFieldMetadata, type LeadCustomFieldRow } from "@/lib/partner-api";

const formSchema = z.object({
  name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  phone: z.string().optional(),
  stage: z.string().min(1, "Selecione um estágio"),
  funnel_id: z.string().min(1, "Selecione um funil"),
  razao_social: z.string().optional(),
  document_type: z.enum(["cpf", "cnpj"]).default("cnpj"),
  document: z.string().optional(),
  cidade_estado: z.string().optional(),
  cep: z.string().optional(),
  endereco: z.string().optional(),
  numero: z.string().optional(),
  complemento: z.string().optional(),
  bairro: z.string().optional(),
  cidade: z.string().optional(),
  uf: z.string().optional(),
  birthday: z.date().optional().nullable(),
  tag_ids: z.array(z.string()).optional(),
});

interface LeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead?: any;
  workspaceId: string;
  funnelId?: string | null;
  workspaceTags?: LeadTag[];
  onLeadCreated?: (leadId: string, leadName: string) => void;
  onDuplicatePhone?: (existingLead: { id: string; name: string }) => void;
}

export function LeadDialog({ open, onOpenChange, lead, workspaceId, funnelId, workspaceTags = [], onLeadCreated, onDuplicatePhone }: LeadDialogProps) {
  const { toast } = useToast();
  const { checkCanCreate } = useResourceLimits(workspaceId);
  const { memberId, permissions } = useMemberPermissions();
  const integrationById = useWorkspaceIntegrationsMap(workspaceId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [funnels, setFunnels] = useState<SalesFunnel[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [countryCode, setCountryCode] = useState("55");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [draftExtraPhones, setDraftExtraPhones] = useState<string[]>([]);
  const [customFieldRows, setCustomFieldRows] = useState<LeadCustomFieldRow[]>([]);
  const [assignSelf, setAssignSelf] = useState(true);
  const showAssignSelf = !lead && Boolean(memberId);
  const assignSelfLocked = permissions?.conversations_visibility === "assigned_only";

  useEffect(() => {
    if (open) setAssignSelf(true);
  }, [open]);

  const selectedCountry = useMemo(
    () => COUNTRY_CODES.find((c) => c.code === countryCode) || COUNTRY_CODES[0],
    [countryCode]
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      stage: "",
      funnel_id: funnelId || "",
      razao_social: "",
      document_type: "cnpj",
      document: "",
      cidade_estado: "",
      cep: "",
      endereco: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      uf: "",
      birthday: null,
      tag_ids: [],
    },
  });

  const selectedFunnelId = form.watch("funnel_id");

  // Fetch funnels
  useEffect(() => {
    if (!open || !workspaceId) return;
    const fetchFunnels = async () => {
      const { data } = await supabase
        .from("sales_funnels")
        .select("id, workspace_id, name, color, is_default, order_index, created_at, integration_id")
        .eq("workspace_id", workspaceId)
        .order("order_index");
      if (data) setFunnels(data as SalesFunnel[]);
    };
    fetchFunnels();
  }, [open, workspaceId]);

  // Fetch stages when funnel changes
  const fetchStages = useCallback(async (fId: string) => {
    if (!fId) { setStages([]); return; }
    const { data } = await supabase
      .from("lead_stages")
      .select("id, name, slug, color, order_index, funnel_id")
      .eq("funnel_id", fId)
      .order("order_index");
    if (data) {
      setStages(data as Stage[]);
      // Auto-select first stage if current stage not in this funnel
      const currentStage = form.getValues("stage");
      const slugs = data.map(s => s.slug);
      if (!currentStage || !slugs.includes(currentStage)) {
        form.setValue("stage", data[0]?.slug || "");
      }
    }
  }, [form]);

  useEffect(() => {
    if (selectedFunnelId) fetchStages(selectedFunnelId);
  }, [selectedFunnelId, fetchStages]);

  // Set default funnel when funnels load
  useEffect(() => {
    if (funnels.length === 0) return;
    const currentFunnel = form.getValues("funnel_id");
    if (!currentFunnel) {
      const target = funnels.find(f => f.id === funnelId) || funnels.find(f => f.is_default) || funnels[0];
      form.setValue("funnel_id", target.id);
    }
  }, [funnels, funnelId, form]);

  useEffect(() => {
    if (lead) {
      const meta = lead.metadata || {};
      const tagIds = (lead.tags as { id: string }[] | undefined)?.map((t) => t.id) ?? [];
      // Detect country code from existing phone
      if (lead.phone) {
        const detected = detectCountryFromPhone(lead.phone);
        setCountryCode(detected.code);
        setPhoneDigits(detected.digits);
      } else {
        setCountryCode("55");
        setPhoneDigits("");
      }
      const existingDoc = meta.cpf || meta.cnpj || "";
      const existingDocType: DocumentType = meta.cpf ? "cpf" : (meta.cnpj ? detectDocumentType(meta.cnpj) : "cnpj");
      form.reset({
        name: lead.name,
        email: lead.email || "",
        phone: lead.phone || "",
        stage: lead.stage,
        funnel_id: lead.funnel_id || funnelId || "",
        razao_social: meta.razao_social || "",
        document_type: existingDocType,
        document: existingDoc ? maskDocument(existingDoc, existingDocType) : "",
        cidade_estado: meta.cidade_estado || "",
        cep: meta.cep || "",
        endereco: meta.endereco || "",
        numero: meta.numero || "",
        complemento: meta.complemento || "",
        bairro: meta.bairro || "",
        cidade: meta.cidade || "",
        uf: meta.uf || "",
        birthday: meta.birthday ? new Date(meta.birthday) : null,
        tag_ids: tagIds,
      });
      setCustomFieldRows(customFieldRowsFromMetadata(meta));
    } else {
      setCountryCode("55");
      setPhoneDigits("");
      form.reset({
        name: "",
        email: "",
        phone: "",
        stage: "",
        funnel_id: funnelId || "",
        razao_social: "",
        document_type: "cnpj",
        document: "",
        cidade_estado: "",
        cep: "",
        endereco: "",
        numero: "",
        complemento: "",
        bairro: "",
        cidade: "",
        uf: "",
        birthday: null,
        tag_ids: [],
      });
      setCustomFieldRows([]);
    }
  }, [lead, form, funnelId]);

  const findPhoneConflict = async (phone: string): Promise<PhoneConflict | null> => {
    const { data, error } = await supabase.rpc("find_lead_phone_conflict" as any, {
      p_workspace_id: workspaceId,
      p_phone: phone,
      p_exclude_lead_id: lead?.id ?? null,
    } as any);
    if (error || !data) return null;
    return data as unknown as PhoneConflict;
  };

  const showPhoneConflict = (conflict: PhoneConflict) => {
    toast({
      title: "Telefone já cadastrado",
      description: describePhoneConflict(conflict),
      variant: "destructive",
      action: conflict.can_see && conflict.lead_id && onDuplicatePhone ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            onOpenChange(false);
            onDuplicatePhone({ id: conflict.lead_id!, name: conflict.name || "Contato" });
          }}
        >
          Abrir lead
        </Button>
      ) : undefined,
    });
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    let attemptedPhone: string | null = null;
    try {
      if (!lead) {
        const check = await checkCanCreate('leads');
        if (!check.can_create) {
          toast({
            title: "Limite de leads atingido",
            description: `Seu plano (${check.plan}) permite até ${check.limit} leads. Você já possui ${check.current}. Faça upgrade para criar mais.`,
            variant: "destructive"
          });
          return;
        }
      }

      const existingMetadata = lead?.metadata || {};
      const docDigits = (values.document || "").replace(/\D+/g, "");
      const metadata: Record<string, any> = {
        ...existingMetadata,
        razao_social: values.razao_social || undefined,
        cidade_estado: values.cidade_estado || undefined,
        cep: values.cep || undefined,
        endereco: values.endereco || undefined,
        numero: values.numero || undefined,
        complemento: values.complemento || undefined,
        bairro: values.bairro || undefined,
        cidade: values.cidade || undefined,
        uf: values.uf ? values.uf.toUpperCase() : undefined,
        birthday: values.birthday ? values.birthday.toISOString().split("T")[0] : undefined,
        cpf: values.document_type === "cpf" && docDigits ? values.document : undefined,
        cnpj: values.document_type === "cnpj" && docDigits ? values.document : undefined,
      };

      // Limpa o campo do tipo oposto e undefineds
      if (values.document_type === "cpf") delete metadata.cnpj;
      if (values.document_type === "cnpj") delete metadata.cpf;
      Object.keys(metadata).forEach(key => {
        if (metadata[key] === undefined) delete metadata[key];
      });
      const mergedMetadata = mergeCustomFieldMetadata(metadata, customFieldRows);

      // Normalize phone: countryCode + digits only. Brasil: remove o "0" de
      // operadora/tronco ("053 9…") e DDI repetido, senão vira 55053… (lead duplicado).
      const normalizedPhone = phoneDigits
        ? countryCode === "55"
          ? normalizeImportPhone(`55${stripBrDialPrefixes(phoneDigits)}`)
          : `${countryCode}${phoneDigits.replace(/^0+/, "")}`
        : null;
      attemptedPhone = normalizedPhone;

      if (normalizedPhone && (!lead || normalizedPhone !== (lead.phone || "").replace(/\D/g, ""))) {
        const conflict = await findPhoneConflict(normalizedPhone);
        if (conflict) {
          showPhoneConflict(conflict);
          return;
        }
      }

      const payload = {
        name: values.name,
        email: values.email || null,
        phone: normalizedPhone,
        stage: values.stage,
        funnel_id: values.funnel_id,
        metadata: mergedMetadata as unknown as Json,
      };

      if (lead) {
        const { error } = await supabase
          .from("leads")
          .update(payload)
          .eq("id", lead.id)
          .eq("workspace_id", workspaceId);

        if (error) throw error;
        if (payload.funnel_id && payload.stage) {
          await persistLeadFunnelPlacement(supabase, lead.id, payload.funnel_id, payload.stage);
        }

        toast({
          title: "Lead atualizado",
          description: "O lead foi atualizado com sucesso.",
        });
      } else {
        const assignToSelf =
          Boolean(memberId) &&
          showAssignSelf &&
          (assignSelfLocked || assignSelf);
        const { data: inserted, error } = await supabase
          .from("leads")
          .insert([
            {
              ...payload,
              workspace_id: workspaceId,
              ...(assignToSelf ? { assigned_member_id: memberId } : {}),
            },
          ] as any)
          .select("id")
          .single();

        if (error) throw error;

        if (inserted?.id && payload.funnel_id && payload.stage) {
          await persistLeadFunnelPlacement(supabase, inserted.id, payload.funnel_id, payload.stage);
        }
        if (inserted?.id && draftExtraPhones.length > 0) {
          await insertDraftLeadPhones(workspaceId, inserted.id, draftExtraPhones);
          setDraftExtraPhones([]);
        }
        const tagIds = values.tag_ids ?? [];
        let tagAssignFailed = false;
        if (inserted?.id && tagIds.length > 0) {
          const { error: tagError } = await supabase.from("lead_tag_assignments").insert(
            tagIds.map((tag_id) => ({ lead_id: inserted.id, tag_id })),
          );
          if (tagError) {
            tagAssignFailed = true;
            toast({
              title: "Lead criado",
              description: "O lead foi criado, mas não foi possível atribuir algumas tags. Você pode adicioná-las depois.",
              variant: "destructive",
            });
          }
        }
        if (!tagAssignFailed) {
          toast({
            title: "Lead criado",
            description: "O lead foi criado com sucesso.",
          });
        }

        // Notify parent about new lead
        if (inserted?.id) {
          onLeadCreated?.(inserted.id, values.name);
        }
      }

      onOpenChange(false);
    } catch (error: any) {
      const msg = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();
      const isDupPhone =
        (error?.code === "23505" || msg.includes("duplicate key")) &&
        msg.includes("idx_leads_workspace_phone");

      if (isDupPhone && attemptedPhone) {
        const conflict = await findPhoneConflict(attemptedPhone);
        showPhoneConflict(conflict ?? { can_see: false, lead_id: null, name: null, owner_display: null, funnel_name: null });
      } else {
        toast({
          title: "Erro",
          description: humanizeSupabaseError(error, {
            permissionMessage: lead
              ? "Você não tem permissão para alterar este lead."
              : "Você não tem permissão para criar este lead.",
            fallback: "Não foi possível salvar o lead. Tente novamente.",
          }),
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-md max-h-[95vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>{lead ? "Editar Lead" : "Novo Lead"}</DialogTitle>
          <DialogDescription>
            {lead
              ? "Atualize as informações do lead"
              : "Adicione um novo lead ao seu pipeline"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl>
                    <Input placeholder="João Silva" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {showAssignSelf && (
              <label className="flex items-start gap-2 rounded-md border p-3 text-sm">
                <Checkbox
                  checked={assignSelfLocked || assignSelf}
                  disabled={assignSelfLocked}
                  onCheckedChange={(c) => setAssignSelf(c === true)}
                  className="mt-0.5"
                />
                <span>
                  Eu serei o responsável por este contato
                  <span className="block text-xs text-muted-foreground">
                    {assignSelfLocked
                      ? "Obrigatório: você só vê os contatos atribuídos a você."
                      : "Vem marcado. Desmarque para criar o contato sem responsável."}
                  </span>
                </span>
              </label>
            )}

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="joao@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={() => (
                <FormItem>
                  <FormLabel>Telefone</FormLabel>
                  <div className="flex gap-2">
                    <Select value={countryCode} onValueChange={setCountryCode}>
                      <SelectTrigger className="w-[110px] shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRY_CODES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            <span className="flex items-center gap-1.5">
                              <span>{c.flag}</span>
                              <span>+{c.code}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormControl>
                      <Input
                        placeholder={selectedCountry.placeholder}
                        value={phoneDigits}
                        onChange={(e) => setPhoneDigits(e.target.value.replace(/\D/g, ""))}
                        inputMode="numeric"
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <LeadExtraPhonesEditor
              workspaceId={workspaceId}
              leadId={lead?.id ?? null}
              draftPhones={draftExtraPhones}
              onDraftPhonesChange={setDraftExtraPhones}
            />

            <FormField
              control={form.control}
              name="razao_social"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Razão Social</FormLabel>
                  <FormControl>
                    <Input placeholder="Empresa LTDA" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="document_type"
              render={({ field }) => {
                const docType = (field.value as DocumentType) || "cnpj";
                return (
                  <FormItem>
                    <FormLabel>Documento</FormLabel>
                    <div className="flex flex-col gap-2">
                      <ToggleGroup
                        type="single"
                        value={docType}
                        onValueChange={(v) => {
                          if (!v) return;
                          field.onChange(v);
                          // Reformata o valor atual com a nova máscara
                          const current = form.getValues("document") || "";
                          form.setValue("document", maskDocument(current, v as DocumentType));
                        }}
                        className="justify-start"
                      >
                        <ToggleGroupItem value="cpf" aria-label="CPF" className="h-8 px-3 text-xs">CPF</ToggleGroupItem>
                        <ToggleGroupItem value="cnpj" aria-label="CNPJ" className="h-8 px-3 text-xs">CNPJ</ToggleGroupItem>
                      </ToggleGroup>
                      <FormField
                        control={form.control}
                        name="document"
                        render={({ field: docField }) => (
                          <FormControl>
                            <Input
                              placeholder={docType === "cpf" ? "000.000.000-00" : "00.000.000/0000-00"}
                              value={(docField.value as string) || ""}
                              onChange={(e) => docField.onChange(maskDocument(e.target.value, docType))}
                              inputMode="numeric"
                            />
                          </FormControl>
                        )}
                      />
                    </div>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
              <h4 className="text-sm font-medium">Endereço</h4>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="cep"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CEP</FormLabel>
                      <FormControl>
                        <Input placeholder="00000-000" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="cidade"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cidade</FormLabel>
                      <FormControl>
                        <Input placeholder="São Paulo" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="uf"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>UF</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="SP"
                          maxLength={2}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_7rem]">
                <FormField
                  control={form.control}
                  name="endereco"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Endereço</FormLabel>
                      <FormControl>
                        <Input placeholder="Rua, avenida..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="numero"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nº do endereço</FormLabel>
                      <FormControl>
                        <Input placeholder="123" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="complemento"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Complemento</FormLabel>
                      <FormControl>
                        <Input placeholder="Apto, sala..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="bairro"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bairro</FormLabel>
                      <FormControl>
                        <Input placeholder="Bairro" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="cidade_estado"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cidade/Estado (legado)</FormLabel>
                    <FormControl>
                      <Input placeholder="São Paulo/SP" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="birthday"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Data de Nascimento</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value ? (
                            format(field.value, "dd/MM/yyyy")
                          ) : (
                            <span>Selecione uma data</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value || undefined}
                        onSelect={field.onChange}
                        disabled={(date) =>
                          date > new Date() || date < new Date("1900-01-01")
                        }
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Funnel Selector */}
            <FormField
              control={form.control}
              name="funnel_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Funil *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um funil" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {funnels.map((funnel) => (
                        <SelectItem key={funnel.id} value={funnel.id}>
                          <div className="flex items-center gap-2">
                            <div className={cn("w-2 h-2 rounded-full", funnel.color)} />
                            {resolveFunnelDisplayName(funnel, integrationById)}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Stage Selector */}
            <FormField
              control={form.control}
              name="stage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Estágio *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um estágio" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {stages.map((stage) => (
                        <SelectItem key={stage.id} value={stage.slug}>
                          <div className="flex items-center gap-2">
                            <div className={cn("w-2 h-2 rounded-full", stage.color)} />
                            {stage.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <LeadCustomFieldsInputs
              rows={customFieldRows}
              onChange={setCustomFieldRows}
            />

            <FormField
              control={form.control}
              name="tag_ids"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tags</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !field.value?.length && "text-muted-foreground",
                          )}
                        >
                          {!field.value?.length
                            ? "Nenhuma tag"
                            : field.value.length === 1
                              ? workspaceTags.find((t) => t.id === field.value?.[0])?.name ?? "1 tag"
                              : `${field.value.length} tags`}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-full min-w-[var(--radix-popover-trigger-width)] p-2" align="start">
                      {workspaceTags.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-2 px-2">Nenhuma tag criada no workspace.</p>
                      ) : (
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {workspaceTags.map((tag) => (
                            <div
                              key={tag.id}
                              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted"
                              onClick={() => {
                                const current = field.value ?? [];
                                const next = current.includes(tag.id)
                                  ? current.filter((id) => id !== tag.id)
                                  : [...current, tag.id];
                                field.onChange(next);
                              }}
                            >
                              <Checkbox
                                checked={(field.value ?? []).includes(tag.id)}
                                onCheckedChange={(checked) => {
                                  const current = field.value ?? [];
                                  const next = checked
                                    ? [...current, tag.id]
                                    : current.filter((id) => id !== tag.id);
                                  field.onChange(next);
                                }}
                              />
                              <div className={cn("w-3 h-3 rounded-full shrink-0", tag.color)} />
                              <span className="text-sm truncate">{tag.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Salvando..." : lead ? "Atualizar" : "Criar"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
